import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";

const PAGE_SIZE = 100;

// Virtual column keys for the two derived columns rendered on this page. They aren't real
// field_data keys - the row renderer special-cases them.
const COL_CASE_DIM = "__case_dimension__";
const COL_QTY_HQ = "__qty_hq__";

// Raw numeric Case_* columns we hide after merging them into "Case Dimension". They stay
// in field_data (and in _search_blob for the backend search) so a numeric query like `46`
// still finds rows; only the on-screen column is removed.
const HIDDEN_FIELDS = new Set(["Case_length", "Case_width", "Case_height"]);

// Display-only header labels. Mongo documents, _search_blob, /fields endpoint, and the
// daily sync all keep the raw FM field names so backend queries stay unambiguous and
// re-syncs aren't required to change a label.
const HEADER_LABELS = {
  "product::英文名称": "英文名称",
  "product::采购价格": "采购价格",
  "product::GT_SKU": "GT_SKU",
  "product::条码": "条码",
  "invoice::shipping date": "shipping date",
  "invoice::customer": "customer",
  [COL_CASE_DIM]: "Case Dimension",
  [COL_QTY_HQ]: "QTY/HQ",
};

// Usable load volume of a 40 HQ container in m^3, per the agreed spec. If this needs to be
// per-user or per-customer later, lift it into settings.
const HQ_USABLE_CBM = 68;

const _toNumber = (v) => {
  if (v === null || v === undefined || v === "") return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

function formatCaseDimension(fd) {
  const l = _toNumber(fd?.Case_length);
  const w = _toNumber(fd?.Case_width);
  const h = _toNumber(fd?.Case_height);
  if (!l || !w || !h) return null;
  // hair spaces around × for readability; drop trailing zeros
  const fmt = (n) => (Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2))));
  return `${fmt(l)}\u200A×\u200A${fmt(w)}\u200A×\u200A${fmt(h)} cm`;
}

function computeQtyHq(fd) {
  const l = _toNumber(fd?.Case_length);
  const w = _toNumber(fd?.Case_width);
  const h = _toNumber(fd?.Case_height);
  const packing = _toNumber(fd?.["case packing"]);
  if (!l || !w || !h || !packing) return null;
  const cbm = (l * w * h) / 1_000_000;
  if (!cbm) return null;
  return Math.floor(HQ_USABLE_CBM / cbm) * packing;
}

/** Local YYYY-MM-DD, used by the <input type="date"> controls below. */
const toInputDate = (iso) => (iso ? iso.slice(0, 10) : "");

export default function BHSellingHistory() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [debouncedFromDate, setDebouncedFromDate] = useState("");
  const [debouncedToDate, setDebouncedToDate] = useState("");
  const [rows, setRows] = useState([]);
  const [fields, setFields] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState({ running: false, phase: null, done: 0, total: 0, last_synced_at: null });
  const pollRef = useRef(null);

  // Popover: at most one 英文名称 cell expanded at a time. Store the recordId of the row
  // whose description cell is currently open; null = closed. Click toggles; outside click
  // or Escape closes.
  const [openDescRow, setOpenDescRow] = useState(null);
  useEffect(() => {
    if (openDescRow === null) return;
    const onDocDown = (e) => {
      if (!e.target.closest || !e.target.closest("[data-desc-popover-root]")) {
        setOpenDescRow(null);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDescRow(null);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openDescRow]);

  // Debounce the search box so typing doesn't fire a query on every keystroke. Pagination
  // reset is handled by the dedicated effect below (which also catches date-range changes).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Same debounce for the two date pickers. Without this, hitting both ends of the range
  // in quick succession fires two overlapping requests - the one with the looser filter
  // takes longer to count and arrives last, overwriting the correct result (classic
  // last-write-wins race).
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFromDate(fromDate);
      setDebouncedToDate(toDate);
    }, 350);
    return () => clearTimeout(t);
  }, [fromDate, toDate]);

  const loadFields = useCallback(async () => {
    try {
      const { data } = await api.get("/bh-history/fields");
      setFields(data.fields || []);
    } catch (e) {
      // Non-fatal: list endpoint also returns field_data so columns will fall back to row-based discovery.
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { offset, limit: PAGE_SIZE };
      if (debouncedSearch) params.q = debouncedSearch;
      if (debouncedFromDate) params.date_from = debouncedFromDate;
      if (debouncedToDate) params.date_to = debouncedToDate;
      const { data } = await api.get("/bh-history", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast({
        title: "Could not load BH Selling History",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [offset, debouncedSearch, debouncedFromDate, debouncedToDate]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/bh-history/sync-status");
      setSyncStatus(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    loadFields();
    loadSyncStatus();
  }, [loadFields, loadSyncStatus]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  // Poll sync-status every 3s while a sync is running, so the progress bar and the "last
  // synced at" badge update live. When the sync finishes, pull a fresh field list + rows
  // one time so the table reflects whatever just landed.
  useEffect(() => {
    if (!syncStatus.running) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(async () => {
      const next = await loadSyncStatus();
      if (next && !next.running) {
        loadFields();
        loadRows();
      }
    }, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [syncStatus.running, loadSyncStatus, loadFields, loadRows]);

  const handleSync = async () => {
    try {
      await api.post("/bh-history/refresh-cache");
      toast({ title: "Sync started" });
      // Nudge status immediately so the polling loop activates.
      loadSyncStatus();
    } catch (e) {
      toast({
        title: "Could not start sync",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    }
  };

  // Reset to page 1 whenever the user changes a server-side filter (search or date range).
  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, debouncedFromDate, debouncedToDate]);

  // Backend now does the date filtering (across all 200k rows). Frontend just displays
  // whatever the current page returned.
  const visibleRows = rows;

  // Columns: pinned leading order (per spec) + Case Dimension + QTY/HQ as derived columns,
  // then every remaining real key (minus the hidden Case_* raws). Pinned keys only appear
  // when they actually exist in /fields or in the current rows, so a layout drop doesn't
  // leave empty columns.
  const PINNED_LEADING = [
    "item #",
    "product::英文名称",
    "case packing",
    COL_CASE_DIM,
    COL_QTY_HQ,
    "price",
    "product::GT_SKU",
  ];
  const columns = useMemo(() => {
    const seen = new Set(fields);
    const extra = [];
    rows.forEach((r) => {
      Object.keys(r.field_data || {}).forEach((k) => {
        if (!seen.has(k)) {
          seen.add(k);
          extra.push(k);
        }
      });
    });
    const real = [...fields, ...extra].filter((k) => !HIDDEN_FIELDS.has(k));
    const realSet = new Set(real);
    const lead = PINNED_LEADING.filter(
      (k) => k === COL_CASE_DIM || k === COL_QTY_HQ || realSet.has(k)
    );
    const leadSet = new Set(lead);
    const rest = real.filter((k) => !leadSet.has(k));
    return [...lead, ...rest];
  }, [fields, rows]);

  const syncProgress = syncStatus.total > 0 ? Math.round((syncStatus.done / syncStatus.total) * 100) : 0;
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="flex h-screen w-screen flex-col bg-white" data-testid="bh-selling-history-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-zinc-200 bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="h-8 w-8 p-0"
            data-testid="bh-history-back-button"
          >
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-base font-bold text-zinc-900">BH Selling History</h1>
          <span className="rounded-sm bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600" data-testid="bh-history-count-label">
            {loading ? "..." : total.toLocaleString()} records
          </span>
          {syncStatus.last_synced_at && (
            <span className="text-xs text-zinc-500" data-testid="bh-history-last-synced-label">
              Last synced: {new Date(syncStatus.last_synced_at).toLocaleString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleSync}
            disabled={syncStatus.running}
            size="sm"
            variant="outline"
            className="rounded-sm border-zinc-300"
            data-testid="bh-history-sync-button"
          >
            <ArrowClockwise size={14} className={`mr-1.5 ${syncStatus.running ? "animate-spin" : ""}`} />
            {syncStatus.running ? "Syncing..." : "Sync Now"}
          </Button>
        </div>
      </div>

      {/* Sync progress bar */}
      {syncStatus.running && (
        <div className="border-b border-zinc-200 bg-blue-50 px-4 py-2" data-testid="bh-history-sync-progress-bar">
          <div className="flex items-center justify-between text-xs text-blue-900">
            <span>
              {syncStatus.phase === "fetching" ? "Fetching from FileMaker..." : "Writing to cache..."} {syncStatus.done.toLocaleString()} /{" "}
              {syncStatus.total.toLocaleString()}
            </span>
            <span className="font-semibold">{syncProgress}%</span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-blue-200">
            <div
              className="h-full rounded-full bg-blue-600 transition-all duration-300"
              style={{ width: `${syncProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-3 border-b border-zinc-200 bg-zinc-50 px-4 py-2">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-zinc-400" />
          <Input
            placeholder="Search all fields..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-72 rounded-sm pl-7 text-sm"
            data-testid="bh-history-search-input"
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-600">
          <span>Shipping date:</span>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(toInputDate(e.target.value))}
            className="h-8 w-36 rounded-sm text-xs"
            data-testid="bh-history-date-from"
          />
          <span>to</span>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(toInputDate(e.target.value))}
            className="h-8 w-36 rounded-sm text-xs"
            data-testid="bh-history-date-to"
          />
          {(fromDate || toDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFromDate("");
                setToDate("");
              }}
              className="h-7 px-2 text-xs"
              data-testid="bh-history-date-clear"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse text-xs" data-testid="bh-history-table">
          <thead className="sticky top-0 z-10 bg-zinc-900 text-white">
            <tr>
              {columns.map((col) => {
                const isDesc = col === "product::英文名称";
                const isQty = col === COL_QTY_HQ;
                return (
                  <th
                    key={col}
                    className={`border-r border-zinc-700 px-2 py-2 font-medium ${
                      isDesc ? "w-[180px] max-w-[180px] text-left" : "whitespace-nowrap"
                    } ${isQty ? "text-right" : "text-left"}`}
                    data-testid={`bh-history-col-${col}`}
                  >
                    {HEADER_LABELS[col] || col}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length || 1} className="px-4 py-6 text-center text-zinc-500">
                  Loading...
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length || 1} className="px-4 py-6 text-center text-zinc-500" data-testid="bh-history-empty-state">
                  {total === 0 && !syncStatus.last_synced_at
                    ? "No data yet — click 'Sync Now' to pull from FileMaker"
                    : "No matching records"}
                </td>
              </tr>
            ) : (
              visibleRows.map((r) => {
                const fd = r.field_data || {};
                return (
                  <tr key={r.recordId} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`bh-history-row-${r.recordId}`}>
                    {columns.map((col) => {
                      // Derived: Case Dimension "L×W×H cm"
                      if (col === COL_CASE_DIM) {
                        const txt = formatCaseDimension(fd);
                        return (
                          <td
                            key={col}
                            className="whitespace-nowrap border-r border-zinc-100 px-2 py-1.5 text-zinc-700"
                            data-testid={`bh-history-case-dim-${r.recordId}`}
                          >
                            {txt || <span className="text-zinc-400">-</span>}
                          </td>
                        );
                      }
                      // Derived: QTY/HQ (pieces per 40 HQ container)
                      if (col === COL_QTY_HQ) {
                        const n = computeQtyHq(fd);
                        return (
                          <td
                            key={col}
                            className="whitespace-nowrap border-r border-zinc-100 px-2 py-1.5 text-right font-bold text-zinc-800"
                            data-testid={`bh-history-qty-hq-${r.recordId}`}
                          >
                            {n == null ? <span className="font-normal text-zinc-400">-</span> : n.toLocaleString()}
                          </td>
                        );
                      }
                      const v = fd[col];
                      const display = v === null || v === undefined || v === "" ? "" : String(v);
                      // Clickable 英文名称 cell with popover
                      if (col === "product::英文名称") {
                        const open = openDescRow === r.recordId;
                        return (
                          <td
                            key={col}
                            className="relative border-r border-zinc-100 px-2 py-1.5 text-zinc-700"
                            data-desc-popover-root
                            data-testid={`bh-history-desc-cell-${r.recordId}`}
                          >
                            <button
                              type="button"
                              onClick={() => setOpenDescRow(open ? null : r.recordId)}
                              className="block w-[180px] max-w-[180px] cursor-pointer truncate text-left outline-none hover:text-blue-700 focus-visible:text-blue-700"
                              data-testid={`bh-history-desc-button-${r.recordId}`}
                            >
                              {display}
                            </button>
                            {open && display && (
                              <div
                                className="absolute left-0 top-full z-20 mt-1 max-w-[320px] whitespace-normal break-words rounded-sm border-2 border-zinc-200 bg-white px-3 py-2 text-xs text-zinc-800 shadow-md"
                                data-testid={`bh-history-desc-popover-${r.recordId}`}
                              >
                                {display}
                              </div>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td
                          key={col}
                          title={display}
                          className="whitespace-nowrap border-r border-zinc-100 px-2 py-1.5 text-zinc-700"
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between border-t border-zinc-200 bg-zinc-50 px-4 py-2 text-xs text-zinc-600">
        <span data-testid="bh-history-pagination-label">
          {pageStart.toLocaleString()}–{pageEnd.toLocaleString()} of {total.toLocaleString()}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0 || loading}
            className="h-7 rounded-sm px-3"
            data-testid="bh-history-prev-page-button"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= total || loading}
            className="h-7 rounded-sm px-3"
            data-testid="bh-history-next-page-button"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
